import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import MuiTable from "@material-ui/core/Table";
import UpdateIcon from "@material-ui/icons/Update";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CloseIcon from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FileDownloadIcon from "@material-ui/icons/GetApp";
import React, { useState } from "react";
import { TravelerModal } from "../../../common/travelerModal";
import { formatDate } from "../../../common/formatDate";
import { getTheme } from "../../../common/getLocalTheme";
import { urls } from "../../../common/urls";
import moment from "moment";
import { useFetchContext } from "../../../common/FetchContext";
import { toast } from "react-toastify";

export const ListSubAssetView = ({ asset_id, data, AssetSub }: any) => {
  const subAsset = [
    "PS285-PowerSupply",
    "PVEL531-Load Card",
    "PVEL061-Digital Multimeter",
  ];
  const [expanded, setExpanded] = useState(false);
  const [showCurrentRun, setShowCurrentRun] = useState(false);
  const [showPreviousRun, setShowPreviousRun] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showMoreRuns, setShowMoreRuns] = useState<Record<string, boolean>>({});

  // PDF Viewer State
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [viewingRun, setViewingRun] = useState<string | null>(null);
  const [downloadingRun, setDownloadingRun] = useState<string | null>(null);

  // File List Dialog State
  const [fileListOpen, setFileListOpen] = useState(false);
  const [activeFilesList, setActiveFilesList] = useState<string[]>([]);
  const [activeFileTitle, setActiveFileTitle] = useState("");
  const [activeRunData, setActiveRunData] = useState<any>(null);
  const [activeViewingFile, setActiveViewingFile] = useState<any>(null);

  const { authAxios } = useFetchContext();

  const unitsToShow = showMore
    ? data?.stressrun_results[0]?.units
    : data?.stressrun_results[0]?.units.slice(0, 6);

  const addTime = (dateString: any, duration: number) => {
    const oldDate = new Date(dateString);
    const newDate = new Date(oldDate.getTime() + duration * 60000);
    return formatDate(newDate.toString());
  };

  const runs = data?.stressrun_results ?? [];
  const previousRuns = data?.previousstressrun_results ?? [];

  const handleOpenFileList = (
    files: any[] | string[],
    title: string,
    runData: any,
  ) => {
    if (!files || files.length === 0) {
      toast.error("No Files Available.");
      return;
    }
    setActiveFilesList(files); // Now contains objects instead of just strings
    setActiveFileTitle(title);
    setActiveRunData(runData);
    setFileListOpen(true);
  };

  const handleViewFile = async (runFileObj: any, runKey: string) => {
    try {
      setActiveViewingFile(runFileObj); // Save reference for header download

      const fileUrl =
        typeof runFileObj === "string"
          ? runFileObj
          : runFileObj?.stress_download_url;
      if (!fileUrl) {
        toast.error("No file download URL available.");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      setViewingRun(runKey);
      setPdfViewerOpen(true);
      setIsPdfLoading(true);

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        throw new Error(
          `Fetch failed: ${response.status} ${response.statusText}`,
        );
      }

      const contentType =
        response.headers.get("content-type") || "application/pdf";
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(
        new Blob([blob], { type: contentType }),
      );

      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to load file.");
      setPdfViewerOpen(false);
    } finally {
      setIsPdfLoading(false);
      setViewingRun(null);
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
    }
    setPdfBlobUrl(null);
  };

  const handleDownload = async (
    runFileObj: any,
    runKey: string,
  ): Promise<void> => {
    try {
      const fileUrl =
        typeof runFileObj === "string"
          ? runFileObj
          : runFileObj?.stress_download_url;
      const fileName =
        typeof runFileObj === "object" ? runFileObj?.filename : "download.pdf";

      if (!fileUrl) {
        toast.error("No file available for download.");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found.");
        return;
      }

      setDownloadingRun(runKey);

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: { Authorization: `Token ${token}` },
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || `run-file-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
      toast.success("File downloaded successfully.");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file.");
    } finally {
      setDownloadingRun(null);
    }
  };

  return (
    <>
      {data && (
        <Paper style={{ margin: "10px", padding: "10px" }} elevation={2}>
          {data?.subassets?.length > 0 ? (
            data.subassets.map((asset: any, index: number) => (
              <Paper
                key={index}
                style={{ margin: "10px 0", padding: "10px" }}
                elevation={1}
              >
                {asset ? asset : "N/A"}
              </Paper>
            ))
          ) : (
            <Paper style={{ margin: "10px 0", padding: "10px" }} elevation={1}>
              There's no assets linked to this asset.
            </Paper>
          )}
          {data?.inUse && runs.length === 0 ? (
            <Paper
              style={{
                marginTop: 15,
                padding: 10,
              }}
              elevation={1}
            >
              {data?.reason || "No run details available."}
            </Paper>
          ) : data?.inUse ? (
            <>
              <Grid>
                {/* CURRENT RUN */}
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    padding: "10px 20px",
                    marginRight: "10px",
                  }}
                  onClick={() => {
                    setShowCurrentRun((prev) => !prev);
                    setShowPreviousRun(false);
                  }}
                  endIcon={<UpdateIcon />}
                >
                  {showCurrentRun
                    ? "Hide Current Run Details"
                    : "View Current Run Details"}
                </Button>

                {/* PREVIOUS RUN */}
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    padding: "10px 20px",
                  }}
                  onClick={() => {
                    setShowPreviousRun((prev) => !prev);
                    setShowCurrentRun(false);
                  }}
                  endIcon={<UpdateIcon />}
                >
                  {showPreviousRun
                    ? "Hide Previous Run Details"
                    : "View Previous Run Details"}
                </Button>
              </Grid>

              {/* CURRENT RUN DETAILS */}
              {showCurrentRun && (
                <>
                  {runs.map((run: any, index: number) => {
                    const units = run?.units || [];

                    const unitsToShowinuse = showMoreRuns[index]
                      ? units
                      : units.slice(0, 6);

                    const runKey = `current-${index}`;

                    return (
                      <Paper
                        key={runKey}
                        elevation={1}
                        style={{
                          marginTop: "15px",
                          padding: "10px",
                        }}
                      >
                        <Grid
                          container
                          direction="column"
                          style={{
                            backgroundColor: "#FEF3F2",
                            borderRadius: 6,
                            padding: "10px",
                          }}
                        >
                          <Grid item>
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <img
                                src="/Icon_Current_Run_Details.svg"
                                alt="Run History icon"
                                style={{
                                  width: 35,
                                  height: 35,
                                  marginRight: 6,
                                }}
                              />
                              <div>
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    color: "#FF0000",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                  }}
                                >
                                  Current Run Details
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ color: "#000000DE" }}
                                >
                                  Run details and timeline information
                                </Typography>
                              </div>
                            </div>
                          </Grid>

                          <Grid item>
                            <Paper
                              style={{
                                marginTop: "10px",
                                padding: "10px",
                                border: "1px solid #E5E7EB",
                                backgroundColor: "#FFFFFF",
                              }}
                              elevation={0}
                            >
                              <Grid container alignItems="center">
                                <Grid item xs>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src="/Icon_Current_Run.svg"
                                      alt="Run History icon"
                                      style={{
                                        width: 30,
                                        height: 30,
                                        marginRight: 8,
                                      }}
                                    />
                                    <div>
                                      <Typography
                                        variant="subtitle1"
                                        style={{
                                          color: "#FF0000",
                                          fontSize: "12px",
                                          marginBottom: "5px",
                                        }}
                                      >
                                        CURRENT RUN
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        style={{ fontWeight: 700 }}
                                      >
                                        {run?.run_name ? run?.run_name : "N/A"}
                                      </Typography>
                                    </div>
                                  </div>
                                </Grid>

                                <Grid item>
                                  <Typography
                                    style={{ color: "#FF0000", fontSize: 12 }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "#FEF3F2",
                                        padding: "3px 8px",
                                        border: "1px solid #FFCACA",
                                        borderRadius: "100px",
                                        fontSize: "13px",
                                        color: "#FF0000",
                                        marginTop: "4px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      in use
                                    </div>
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Grid container spacing={4} style={{ padding: "10px" }}>
                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "10px",
                              }}
                            >
                              <AccessTimeIcon
                                style={{
                                  marginRight: "6px",
                                  color: "#0000008A",
                                }}
                              />
                              Timeline
                            </Typography>

                            <Paper
                              style={{ padding: "10px", marginBottom: "10px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_StartDate.svg"
                                  alt="status icon"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 8,
                                  }}
                                />
                                <div>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      fontSize: "12px",
                                      color: "#0000008A",
                                    }}
                                  >
                                    START DATE
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ fontWeight: 700 }}
                                  >
                                    {run?.start_date
                                      ? formatDate(run.start_date)
                                      : "N/A"}
                                  </Typography>
                                </div>
                              </div>
                            </Paper>

                            <Paper
                              style={{ padding: "10px", marginBottom: "10px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_EndDate.svg"
                                  alt="End icon"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 8,
                                  }}
                                />
                                <div>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      fontSize: "12px",
                                      color: "#0000008A",
                                    }}
                                  >
                                    ESTIMATED END DATE
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ fontWeight: 700 }}
                                  >
                                    {run?.start_date
                                      ? addTime(run.start_date, run.duration)
                                      : "N/A"}
                                  </Typography>
                                </div>
                              </div>
                            </Paper>

                            <Grid>
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={getTheme() || viewingRun === runKey}
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.run_files ?? [],
                                    `Stress Run Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View Stress run file
                              </Button>
                            </Grid>

                            <Grid>
                              <Button
                                variant="contained"
                                color="primary"
                                style={{ marginTop: 5 }}
                                disabled={
                                  getTheme() || viewingRun === `${runKey}-log`
                                }
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.log_run_files ?? run?.log_files ?? [],
                                    `Stress Log Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View Stress Log File
                              </Button>
                            </Grid>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <img
                                src="/Icon_Details.svg"
                                alt="status icon"
                                style={{
                                  width: 22,
                                  height: 22,
                                  marginRight: 6,
                                }}
                              />
                              Details
                            </Typography>
                            <Paper
                              style={{ padding: "10px", marginBottom: "10px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_Procedure.svg"
                                  alt="Procedure icon"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 8,
                                  }}
                                />
                                <div>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      fontSize: "12px",
                                      color: "#0000008A",
                                    }}
                                  >
                                    PROCEDURE
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ fontWeight: 700 }}
                                  >
                                    {run?.procedure_definition
                                      ? run.procedure_definition
                                      : "N/A"}
                                  </Typography>
                                </div>
                              </div>
                            </Paper>

                            <Paper
                              style={{ padding: "10px", marginBottom: "10px" }}
                            >
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src="/Icon_Units.svg"
                                      alt="Unit icon"
                                      style={{
                                        width: 30,
                                        height: 30,
                                        marginRight: 8,
                                      }}
                                    />
                                    <Typography
                                      variant="body1"
                                      style={{
                                        color: "#0000008A",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      UNITS CURRENTLY IN USE
                                    </Typography>
                                  </div>

                                  <Typography>
                                    <div
                                      style={{
                                        backgroundColor: "#FFF6E5",
                                        padding: "3px 8px",
                                        border: "1px solid #FFD27F",
                                        borderRadius: "100px",
                                        fontSize: "13px",
                                        color: "#FFA500",
                                        marginTop: "4px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {`${run?.units?.length ? run?.units?.length : 0} Units`}
                                    </div>
                                  </Typography>
                                </div>

                                <Grid
                                  container
                                  spacing={1}
                                  style={{ marginRight: "18px" }}
                                >
                                  {unitsToShowinuse?.map(
                                    (unit: any, index: number) => (
                                      <Grid item key={index}>
                                        <TravelerModal serialNumber={unit} />
                                      </Grid>
                                    ),
                                  )}

                                  {run?.units?.length > 6 && (
                                    <Grid item xs={12}>
                                      <Button
                                        color="primary"
                                        style={{
                                          marginTop: "6px",
                                          fontSize: 13,
                                          fontWeight: 500,
                                          textTransform: "none",
                                        }}
                                        onClick={() =>
                                          setShowMoreRuns((prev) => ({
                                            ...prev,
                                            [index]: !prev[index],
                                          }))
                                        }
                                      >
                                        {showMoreRuns[index]
                                          ? "- Show Less"
                                          : `+ Show ${run?.units?.length - 6} More`}
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>
                              </div>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </>
              )}

              {/* PREVIOUS RUN DETAILS */}
              {showPreviousRun && (
                <>
                  {previousRuns.length > 0 ? (
                    previousRuns.map((run: any, index: number) => {
                      const units = run?.units || [];
                      const runKey = `previous-${index}`;
                      const unitsToShowinuse = showMoreRuns[runKey]
                        ? units
                        : units.slice(0, 6);
                      return (
                        <Paper
                          key={runKey}
                          elevation={1}
                          style={{ marginTop: "15px", padding: "10px" }}
                        >
                          {/* PREVIOUS RUN HEADER */}
                          <Grid
                            container
                            direction="column"
                            style={{
                              backgroundColor: "#EFFDF4",
                              borderRadius: 6,
                              padding: "10px",
                              border: "1px solid #B9F8CF",
                            }}
                          >
                            <Grid item>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_Run_History.svg"
                                  alt="Run History icon"
                                  style={{
                                    width: 35,
                                    height: 35,
                                    marginRight: 6,
                                  }}
                                />
                                <div>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      color: "#008000",
                                      fontWeight: 600,
                                      fontSize: "16px",
                                    }}
                                  >
                                    Previous Run Details
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ color: "#000000DE" }}
                                  >
                                    Previous run details and timeline
                                    information
                                  </Typography>
                                </div>
                              </div>
                            </Grid>
                            {/* RUN NAME */}
                            <Grid item>
                              <Paper
                                style={{
                                  marginTop: "10px",
                                  padding: "10px",
                                  border: "1px solid #E5E7EB",
                                  backgroundColor: "#FFFFFF",
                                }}
                                elevation={0}
                              >
                                <Grid container alignItems="center">
                                  <Grid item xs>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <img
                                        src="/Icon_Last_Run.svg"
                                        alt="Previous Run"
                                        style={{
                                          width: 30,
                                          height: 30,
                                          marginRight: 8,
                                        }}
                                      />
                                      <div>
                                        <Typography
                                          variant="subtitle1"
                                          style={{
                                            color: "#008000",
                                            fontSize: "12px",
                                            marginBottom: "5px",
                                          }}
                                        >
                                          PREVIOUS RUN
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          style={{ fontWeight: 700 }}
                                        >
                                          {run?.run_name || "N/A"}
                                        </Typography>
                                      </div>
                                    </div>
                                  </Grid>
                                  <Grid item>
                                    <div
                                      style={{
                                        backgroundColor: "#EFFDF4",
                                        padding: "3px 8px",
                                        border: "1px solid #B9F8CF",
                                        borderRadius: "100px",
                                        fontSize: "13px",
                                        color: "#008000",
                                        marginTop: "4px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      completed
                                    </div>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                          {/* TIMELINE + DETAILS */}
                          <Grid
                            container
                            spacing={4}
                            style={{ padding: "10px" }}
                          >
                            {/* TIMELINE */}
                            <Grid item xs={6}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                }}
                              >
                                <AccessTimeIcon
                                  style={{
                                    marginRight: "6px",
                                    color: "#0000008A",
                                  }}
                                />{" "}
                                Timeline
                              </Typography>
                              {/* START DATE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  START DATE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.start_date
                                    ? formatDate(run.start_date)
                                    : "N/A"}
                                </Typography>
                              </Paper>
                              {/* END DATE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  END DATE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.end_date
                                    ? formatDate(run.end_date)
                                    : "N/A"}
                                </Typography>
                              </Paper>
                              {/* DOWNLOAD */}
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.run_files ?? [],
                                    `Stress Run Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View stress run file
                              </Button>

                              <Button
                                variant="contained"
                                color="primary"
                                style={{ marginTop: 5 }}
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.log_run_files ?? run?.log_files ?? [],
                                    `Stress Log Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View Stress Log File
                              </Button>
                            </Grid>
                            {/* DETAILS */}
                            <Grid item xs={6}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_Details.svg"
                                  alt="Details"
                                  style={{
                                    width: 22,
                                    height: 22,
                                    marginRight: 6,
                                  }}
                                />
                                Details
                              </Typography>
                              {/* PROCEDURE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  PROCEDURE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.procedure_definition || "N/A"}
                                </Typography>
                              </Paper>
                              {/* UNITS */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src="/Icon_Units.svg"
                                      alt="Unit"
                                      style={{
                                        width: 30,
                                        height: 30,
                                        marginRight: 8,
                                      }}
                                    />
                                    <Typography
                                      variant="body1"
                                      style={{
                                        color: "#0000008A",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      UNITS IN PREVIOUS RUN
                                    </Typography>
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#FFF6E5",
                                      padding: "3px 8px",
                                      border: "1px solid #FFD27F",
                                      borderRadius: "100px",
                                      fontSize: "13px",
                                      color: "#FFA500",
                                    }}
                                  >
                                    {units.length} Units
                                  </div>
                                </div>
                                <Grid container spacing={1}>
                                  {/* UNITS LIST */}
                                  {unitsToShowinuse.map(
                                    (unit: any, unitIndex: number) => (
                                      <Grid item key={unitIndex}>
                                        <TravelerModal serialNumber={unit} />
                                      </Grid>
                                    ),
                                  )}
                                  {/* SHOW MORE */}
                                  {units.length > 6 && (
                                    <Grid item xs={12}>
                                      <Button
                                        color="primary"
                                        style={{
                                          marginTop: "6px",
                                          fontSize: 13,
                                          fontWeight: 500,
                                          textTransform: "none",
                                        }}
                                        onClick={() =>
                                          setShowMoreRuns((prev) => ({
                                            ...prev,
                                            [runKey]: !prev[runKey],
                                          }))
                                        }
                                      >
                                        {showMoreRuns[runKey]
                                          ? "- Show Less"
                                          : `+ Show ${units.length - 6} More`}{" "}
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })
                  ) : (
                    /* NO PREVIOUS RUN */ <Typography
                      style={{
                        marginTop: 16,
                        padding: 16,
                        textAlign: "center",
                        color: "#777",
                      }}
                    >
                      No previous run details available.
                    </Typography>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ padding: "10px 20px" }}
                  onClick={() => {
                    setShowPreviousRun((prev) => !prev);
                    setShowCurrentRun(false);
                  }}
                  endIcon={<UpdateIcon />}
                >
                  {showPreviousRun
                    ? "Hide Last Run History"
                    : "View Last Run History"}
                </Button>
              </Grid>

              {showPreviousRun && (
                <>
                  {previousRuns.length > 0 ? (
                    previousRuns.map((run: any, index: number) => {
                      const units = run?.units || [];
                      const runKey = `previous-${index}`;
                      const unitsToShowinuse = showMoreRuns[runKey]
                        ? units
                        : units.slice(0, 6);
                      return (
                        <Paper
                          key={runKey}
                          elevation={1}
                          style={{ marginTop: "15px", padding: "10px" }}
                        >
                          {/* PREVIOUS RUN HEADER */}
                          <Grid
                            container
                            direction="column"
                            style={{
                              backgroundColor: "#EFFDF4",
                              borderRadius: 6,
                              padding: "10px",
                              border: "1px solid #B9F8CF",
                            }}
                          >
                            <Grid item>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_Run_History.svg"
                                  alt="Run History icon"
                                  style={{
                                    width: 35,
                                    height: 35,
                                    marginRight: 6,
                                  }}
                                />
                                <div>
                                  <Typography
                                    variant="subtitle1"
                                    style={{
                                      color: "#008000",
                                      fontWeight: 600,
                                      fontSize: "16px",
                                    }}
                                  >
                                    Previous Run Details
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    style={{ color: "#000000DE" }}
                                  >
                                    Previous run details and timeline
                                    information
                                  </Typography>
                                </div>
                              </div>
                            </Grid>
                            {/* RUN NAME */}
                            <Grid item>
                              <Paper
                                style={{
                                  marginTop: "10px",
                                  padding: "10px",
                                  border: "1px solid #E5E7EB",
                                  backgroundColor: "#FFFFFF",
                                }}
                                elevation={0}
                              >
                                <Grid container alignItems="center">
                                  <Grid item xs>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <img
                                        src="/Icon_Last_Run.svg"
                                        alt="Previous Run"
                                        style={{
                                          width: 30,
                                          height: 30,
                                          marginRight: 8,
                                        }}
                                      />
                                      <div>
                                        <Typography
                                          variant="subtitle1"
                                          style={{
                                            color: "#008000",
                                            fontSize: "12px",
                                            marginBottom: "5px",
                                          }}
                                        >
                                          PREVIOUS RUN
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          style={{ fontWeight: 700 }}
                                        >
                                          {run?.run_name || "N/A"}
                                        </Typography>
                                      </div>
                                    </div>
                                  </Grid>
                                  <Grid item>
                                    <div
                                      style={{
                                        backgroundColor: "#EFFDF4",
                                        padding: "3px 8px",
                                        border: "1px solid #B9F8CF",
                                        borderRadius: "100px",
                                        fontSize: "13px",
                                        color: "#008000",
                                        marginTop: "4px",
                                        lineHeight: 1,
                                      }}
                                    >
                                      completed
                                    </div>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                          {/* TIMELINE + DETAILS */}
                          <Grid
                            container
                            spacing={4}
                            style={{ padding: "10px" }}
                          >
                            {/* TIMELINE */}
                            <Grid item xs={6}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                }}
                              >
                                <AccessTimeIcon
                                  style={{
                                    marginRight: "6px",
                                    color: "#0000008A",
                                  }}
                                />{" "}
                                Timeline
                              </Typography>
                              {/* START DATE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  START DATE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.start_date
                                    ? formatDate(run.start_date)
                                    : "N/A"}
                                </Typography>
                              </Paper>
                              {/* END DATE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  END DATE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.end_date
                                    ? formatDate(run.end_date)
                                    : "N/A"}
                                </Typography>
                              </Paper>
                              {/* DOWNLOAD */}
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.run_files ?? [],
                                    `Stress Run Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View stress run file
                              </Button>

                              <Button
                                variant="contained"
                                color="primary"
                                style={{ marginTop: 5 }}
                                onClick={() =>
                                  handleOpenFileList(
                                    run?.log_run_files ?? run?.log_files ?? [],
                                    `Stress Log Files - ${run?.run_name || "Run"}`,
                                    run,
                                  )
                                }
                                startIcon={<VisibilityIcon />}
                              >
                                View Stress Log File
                              </Button>
                            </Grid>
                            {/* DETAILS */}
                            <Grid item xs={6}>
                              <Typography
                                variant="subtitle1"
                                gutterBottom
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <img
                                  src="/Icon_Details.svg"
                                  alt="Details"
                                  style={{
                                    width: 22,
                                    height: 22,
                                    marginRight: 6,
                                  }}
                                />
                                Details
                              </Typography>
                              {/* PROCEDURE */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontSize: "12px",
                                    color: "#0000008A",
                                  }}
                                >
                                  PROCEDURE
                                </Typography>
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 700 }}
                                >
                                  {run?.procedure_definition || "N/A"}
                                </Typography>
                              </Paper>
                              {/* UNITS */}
                              <Paper
                                style={{
                                  padding: "10px",
                                  marginBottom: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src="/Icon_Units.svg"
                                      alt="Unit"
                                      style={{
                                        width: 30,
                                        height: 30,
                                        marginRight: 8,
                                      }}
                                    />
                                    <Typography
                                      variant="body1"
                                      style={{
                                        color: "#0000008A",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      UNITS IN PREVIOUS RUN
                                    </Typography>
                                  </div>
                                  <div
                                    style={{
                                      backgroundColor: "#FFF6E5",
                                      padding: "3px 8px",
                                      border: "1px solid #FFD27F",
                                      borderRadius: "100px",
                                      fontSize: "13px",
                                      color: "#FFA500",
                                    }}
                                  >
                                    {units.length} Units
                                  </div>
                                </div>
                                <Grid container spacing={1}>
                                  {/* UNITS LIST */}
                                  {unitsToShowinuse.map(
                                    (unit: any, unitIndex: number) => (
                                      <Grid item key={unitIndex}>
                                        <TravelerModal serialNumber={unit} />
                                      </Grid>
                                    ),
                                  )}
                                  {/* SHOW MORE */}
                                  {units.length > 6 && (
                                    <Grid item xs={12}>
                                      <Button
                                        color="primary"
                                        style={{
                                          marginTop: "6px",
                                          fontSize: 13,
                                          fontWeight: 500,
                                          textTransform: "none",
                                        }}
                                        onClick={() =>
                                          setShowMoreRuns((prev) => ({
                                            ...prev,
                                            [runKey]: !prev[runKey],
                                          }))
                                        }
                                      >
                                        {showMoreRuns[runKey]
                                          ? "- Show Less"
                                          : `+ Show ${units.length - 6} More`}{" "}
                                      </Button>
                                    </Grid>
                                  )}
                                </Grid>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })
                  ) : (
                    /* NO PREVIOUS RUN */ <Typography
                      style={{
                        marginTop: 16,
                        padding: 16,
                        textAlign: "center",
                        color: "#777",
                      }}
                    >
                      {data?.reason || "No previous run details available."}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* FILE LIST DIALOG AS A TABLE */}
      <Dialog
        open={fileListOpen}
        onClose={() => setFileListOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{activeFileTitle}</Typography>
            <IconButton onClick={() => setFileListOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {activeFilesList.length > 0 ? (
            <MuiTable style={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: "bold" }}>Filename</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>Run-Name</TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Upload Date
                  </TableCell>
                  <TableCell style={{ fontWeight: "bold" }}>
                    Uploaded By
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeFilesList.map((fileItem: any, index: number) => {
                  // Handle both object-based API responses and plain string fallback
                  const isObj =
                    typeof fileItem === "object" && fileItem !== null;
                  const filename = isObj
                    ? fileItem.filename
                    : `File #${index + 1}`;
                  // Access run_name (handling potential trailing space in keys)
                  const runName = isObj
                    ? fileItem["run_name "] ||
                      fileItem.run_name ||
                      activeRunData?.procedure_definition ||
                      "N/A"
                    : activeRunData?.procedure_definition || "N/A";

                  const uploadDate =
                    isObj && fileItem.run_date
                      ? formatDate(fileItem.run_date)
                      : activeRunData?.start_date
                        ? formatDate(activeRunData.start_date)
                        : "N/A";

                  const uploader =
                    isObj && fileItem.user
                      ? fileItem.user
                      : activeRunData?.username || "System";
                  const fileKey = `file-${index}`;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography
                          variant="subtitle2"
                          style={{ fontWeight: 600 }}
                        >
                          {filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{runName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{uploadDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{uploader}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          style={{ gap: 4 }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            startIcon={<VisibilityIcon />}
                            onClick={() => {
                              setFileListOpen(false);
                              handleViewFile(fileItem, `file-${index}`);
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              style={{ padding: 20 }}
            >
              No files found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF VIEWER DIALOG WITH DOWNLOAD BUTTON */}
      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: { height: "90vh", display: "flex", flexDirection: "column" },
        }}
      >
        <DialogTitle disableTypography style={{ padding: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            padding="16px 24px"
          >
            <Typography variant="h6">Run File Viewer</Typography>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {activeViewingFile && (
                <IconButton
                  color="primary"
                  title="Download File"
                  disabled={downloadingRun === "pdf-viewer"}
                  onClick={() =>
                    handleDownload(activeViewingFile, "pdf-viewer")
                  }
                >
                  <FileDownloadIcon />
                </IconButton>
              )}
              <IconButton edge="end" onClick={handleClosePdfViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0, flexGrow: 1 }}>
          {isPdfLoading ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <CircularProgress />
              <Typography style={{ marginTop: 16, color: "#555" }}>
                Preparing your document. This might take a moment...
              </Typography>
            </Box>
          ) : pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF Viewer"
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Typography color="error">No document loaded.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
